import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack } from '@tamagui/stacks';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '@/types/navigation';
import type { MeetingAnnouncementSearchParams } from '@/api/types';
import { Header, Screen, SwipeCard } from '@/components';
import ActionsBar from './HomeScreen/ActionsBar';
import FilterSheet from './HomeScreen/components/FilterSheet';
import { usePrefetchedAnnouncements } from '@/api/hooks/usePrefetchedAnnouncements';
import { COLORS } from '@/constants/colors';

const BOTTOM_PADDING = 16;

const HomeScreen = React.memo(() => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const [index, setIndex] = useState(0);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [searchParams, setSearchParams] =
    useState<MeetingAnnouncementSearchParams>({});

  // Fetch announcements from API with prefetching
  const {
    profiles: rawProfiles,
    announcements: rawAnnouncements,
    isLoading: isAnnouncementsLoading,
    isError,
    refetch,
  } = usePrefetchedAnnouncements({
    params: searchParams,
    currentIndex: index,
  });

  // Filter out announcements user has already applied to
  const announcements = useMemo(() => {
    if (!Array.isArray(rawAnnouncements)) return [];
    return rawAnnouncements.filter(a => !a.hasApplied);
    //return rawAnnouncements;
  }, [rawAnnouncements]);

  // Filter profiles to match filtered announcements
  const profiles = useMemo(() => {
    if (!Array.isArray(rawProfiles)) return [];
    const announcementIds = new Set(announcements.map(a => a.id));
    return rawProfiles.filter(p => announcementIds.has(p.id));
  }, [rawProfiles, announcements]);

  const isLoading = isAnnouncementsLoading;

  const incrementIndex = useCallback(() => {
    if (!Array.isArray(profiles) || profiles.length === 0) return;
    setIndex(prev => (prev + 1) % profiles.length);
  }, [profiles]);

  // Get current announcement ID for navigation - must be before callbacks that use it
  const currentAnnouncement =
    Array.isArray(announcements) && announcements.length > 0
      ? announcements[index]
      : undefined;
  const currentProfile =
    Array.isArray(profiles) && profiles.length > 0
      ? profiles[index]
      : undefined;

  const handleDiscard = useCallback(() => {
    incrementIndex();
  }, [incrementIndex]);

  const handleLike = useCallback(() => {
    if (currentAnnouncement?.id) {
      navigation.navigate('ApplicationCreate', {
        announcementId: currentAnnouncement.id,
        announcement: currentAnnouncement, // Pass full object to avoid refetch
      });
    }
  }, [navigation, currentAnnouncement]);

  const handleFavorite = useCallback(() => {
    // TODO: Implement favorite action via API
    incrementIndex();
  }, [incrementIndex]);

  const handleWalletPress = useCallback(() => {
    navigation.navigate('Wallet');
  }, [navigation]);

  const handleFilterPress = useCallback(() => {
    setFilterSheetOpen(true);
  }, []);

  const handleApplyFilters = useCallback(
    (params: MeetingAnnouncementSearchParams) => {
      setSearchParams(params);
      setIndex(0); // Reset to first profile when filters change
      setFilterSheetOpen(false);
    },
    [],
  );

  // Card container - flex on mobile, min height on web for scrollable layout
  const webCardContainerStyle =
    Platform.OS === 'web' ? { minHeight: 500 } : {};

  const content = (
    <>
      <Header
        onPressLeft={handleWalletPress}
        onPressRight={handleFilterPress}
      />
      <YStack
        flex={1}
        padding="$3"
        paddingBottom={BOTTOM_PADDING}
        gap="$2"
      >
        <YStack flex={1} style={webCardContainerStyle}>
          {isLoading ? (
            <YStack flex={1} alignItems="center" justifyContent="center">
              <ActivityIndicator size="large" color={COLORS.PRIMARY} />
              <Text fontSize={14} color="$color" opacity={0.7} marginTop="$3">
                Ładowanie ogłoszeń...
              </Text>
            </YStack>
          ) : isError ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              gap="$3"
            >
              <Text fontSize={16} color="$color" opacity={0.7}>
                Nie udało się pobrać ogłoszeń
              </Text>
              <Text
                fontSize={14}
                color={COLORS.PRIMARY}
                onPress={() => refetch()}
              >
                Spróbuj ponownie
              </Text>
            </YStack>
          ) : Array.isArray(profiles) &&
            profiles.length > 0 &&
            currentProfile ? (
            <SwipeCard
              profile={currentProfile}
              announcementId={currentAnnouncement?.id}
              announcement={currentAnnouncement}
            />
          ) : (
            <YStack flex={1} alignItems="center" justifyContent="center">
              <Text fontSize={16} color="$color" opacity={0.7}>
                Brak ogłoszeń do wyświetlenia
              </Text>
            </YStack>
          )}
        </YStack>
        <ActionsBar
          onDiscard={handleDiscard}
          onLike={handleLike}
          onFavorite={handleFavorite}
        />
      </YStack>
    </>
  );

  return (
    <Screen>
      {Platform.OS === 'web' ? (
        <ScrollView 
          style={{ flex: 1 }} 
          contentContainerStyle={{ minHeight: '100%' }}
          showsVerticalScrollIndicator={false}
        >
          {content}
        </ScrollView>
      ) : (
        content
      )}

      {/* Filter Sheet */}
      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        isDark={isDark}
        onApplyFilters={handleApplyFilters}
      />
    </Screen>
  );
});

HomeScreen.displayName = 'HomeScreen';

export default HomeScreen;
